/**
 * DocumentCategoriesPage Component
 * 
 * Página de listagem e gerenciamento de categorias de documentos.
 * Permite visualizar, criar, editar e excluir categorias de documentos.
 * 
 * Funcionalidades:
 * - Listagem de categorias
 * - Filtros por tipo (company/user) e status
 * - Modal de criação/edição
 * - Validação de campos
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Edit,
  Trash2,
  Plus,
  Search,
  Tag,
  Building2,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react'
import DataTable from '../../../components/ERP/DataTable/DataTable'
import Modal from '../../../components/ERP/Modal/Modal'
import Button from '../../../components/Button/Button'
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal'
import { documentCategoryService } from '../../../services/documentsApi'
import './DocumentCategoriesPage.css'

function DocumentCategoriesPage() {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [categoryToDelete, setCategoryToDelete] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'user',
    required: false,
    expiry_days: '',
    active: true,
    allow_multiple: false
  })
  const [errors, setErrors] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    active: ''
  })
  const [saving, setSaving] = useState(false)

  const loadCategories = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const cleanFilters = {}
      if (filters.search && filters.search.trim()) {
        cleanFilters.search = filters.search.trim()
      }
      if (filters.type) {
        cleanFilters.type = filters.type
      }
      if (filters.active !== '') {
        cleanFilters.active = filters.active === 'true'
      }

      const response = await documentCategoryService.getAll(cleanFilters)
      if (response && response.success) {
        const categoriesData = response.data?.categories || 
                              (Array.isArray(response.data) ? response.data : [])
        setCategories(categoriesData)
      } else {
        const errorMessage = response?.message || 'Erro ao carregar categorias'
        setError(errorMessage)
        setCategories([])
      }
    } catch (error) {
      console.error('Erro ao carregar categorias de documentos:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao carregar categorias de documentos'
      setError(errorMessage)
      setCategories([])
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category)
      setFormData({
        name: category.name || '',
        description: category.description || '',
        type: category.type || 'user',
        required: category.required || false,
        expiry_days: category.expiry_days || '',
        active: category.active !== undefined ? category.active : true,
        allow_multiple: category.allow_multiple || false
      })
    } else {
      setEditingCategory(null)
      setFormData({
        name: '',
        description: '',
        type: 'user',
        required: false,
        expiry_days: '',
        active: true,
        allow_multiple: false
      })
    }
    setErrors({})
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingCategory(null)
    setFormData({
      name: '',
      description: '',
      type: 'user',
      required: false,
      expiry_days: '',
      active: true
    })
    setErrors({})
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)

    // Validação
    const newErrors = {}
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório'
    }
    if (formData.expiry_days && (isNaN(formData.expiry_days) || parseInt(formData.expiry_days) < 1)) {
      newErrors.expiry_days = 'Dias de expiração deve ser um número positivo'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setSaving(false)
      return
    }

    try {
      const dataToSend = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        type: formData.type,
        required: formData.required,
        expiry_days: formData.expiry_days ? parseInt(formData.expiry_days) : null,
        active: formData.active,
        allow_multiple: formData.allow_multiple
      }

      let response
      if (editingCategory) {
        response = await documentCategoryService.update(editingCategory.id, dataToSend)
      } else {
        response = await documentCategoryService.create(dataToSend)
      }

      if (response && response.success) {
        handleCloseModal()
        await loadCategories()
      } else {
        setErrors({ submit: response?.message || 'Erro ao salvar categoria' })
      }
    } catch (error) {
      console.error('Erro ao salvar categoria:', error)
      setErrors({ submit: error.response?.data?.message || error.message || 'Erro ao salvar categoria' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = (category) => {
    setCategoryToDelete(category)
    setDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    if (!categoryToDelete) return

    try {
      const response = await documentCategoryService.delete(categoryToDelete.id)
      if (response && response.success) {
        setDeleteModalOpen(false)
        setCategoryToDelete(null)
        await loadCategories()
      } else {
        alert(response?.message || 'Erro ao deletar categoria')
      }
    } catch (error) {
      console.error('Erro ao deletar categoria:', error)
      alert(error.response?.data?.message || error.message || 'Erro ao deletar categoria')
    }
  }

  const columns = [
    {
      key: 'name',
      label: 'Nome',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Tag size={16} />
          <strong>{value}</strong>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {value === 'company' ? (
            <>
              <Building2 size={16} />
              <span>Empresa</span>
            </>
          ) : (
            <>
              <User size={16} />
              <span>Usuário</span>
            </>
          )}
        </div>
      )
    },
    {
      key: 'required',
      label: 'Obrigatório',
      render: (value) => (
        value ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#ef4444' }}>
            <AlertTriangle size={14} />
            Sim
          </span>
        ) : (
          <span style={{ color: '#6b7280' }}>Não</span>
        )
      )
    },
    {
      key: 'expiry_days',
      label: 'Dias para Expirar',
      render: (value) => value ? `${value} dias` : '-'
    },
    {
      key: 'allow_multiple',
      label: 'Múltiplos Arquivos',
      render: (value) => (
        value ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#10b981' }}>
            <CheckCircle size={14} />
            Permitido
          </span>
        ) : (
          <span style={{ color: '#6b7280' }}>Não permitido</span>
        )
      )
    },
    {
      key: 'active',
      label: 'Status',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          {value ? (
            <>
              <CheckCircle size={14} style={{ color: '#10b981' }} />
              <span style={{ color: '#10b981' }}>Ativo</span>
            </>
          ) : (
            <>
              <XCircle size={14} style={{ color: '#ef4444' }} />
              <span style={{ color: '#ef4444' }}>Inativo</span>
            </>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => handleOpenModal(row)}
            style={{
              padding: '0.5rem',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#3b82f6'
            }}
            title="Editar"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDelete(row)}
            style={{
              padding: '0.5rem',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#ef4444'
            }}
            title="Excluir"
          >
            <Trash2 size={18} />
          </button>
        </div>
      )
    }
  ]

  return (
    <div className="document-categories-page">
      <div className="document-categories-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Tag size={28} />
          <div>
            <h1>Categorias de Documentos</h1>
            <p>Gerencie as categorias de documentos da empresa e dos usuários</p>
          </div>
        </div>
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Plus size={20} />
          Nova Categoria
        </Button>
      </div>

      {/* Filtros */}
      <div className="document-categories-filters">
        <div className="filter-group">
          <Search size={18} />
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
        </div>
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.875rem'
          }}
        >
          <option value="">Todos os tipos</option>
          <option value="company">Empresa</option>
          <option value="user">Usuário</option>
        </select>
        <select
          value={filters.active}
          onChange={(e) => setFilters({ ...filters, active: e.target.value })}
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.875rem'
          }}
        >
          <option value="">Todos os status</option>
          <option value="true">Ativo</option>
          <option value="false">Inativo</option>
        </select>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center', padding: '2rem' }}>
          <Loader2 size={20} className="spinning" />
          <span>Carregando categorias...</span>
        </div>
      ) : error ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
          {error}
        </div>
      ) : (
        <DataTable
          data={categories}
          columns={columns}
          emptyMessage="Nenhuma categoria encontrada"
        />
      )}

      {/* Modal de Criação/Edição */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
        size="medium"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="spinning" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Salvar
                </>
              )}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Nome <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: RG, CPF, CNH..."
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: errors.name ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
              {errors.name && (
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
                  {errors.name}
                </span>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição da categoria..."
                disabled={saving}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
              />
              {errors.description && (
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
                  {errors.description}
                </span>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Tipo <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: errors.type ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="company">Empresa</option>
                <option value="user">Usuário</option>
              </select>
              {errors.type && (
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
                  {errors.type}
                </span>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Dias para Expirar
              </label>
              <input
                type="number"
                value={formData.expiry_days}
                onChange={(e) => setFormData({ ...formData, expiry_days: e.target.value })}
                placeholder="Ex: 365 (opcional)"
                disabled={saving}
                min="1"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: errors.expiry_days ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
              {errors.expiry_days && (
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
                  {errors.expiry_days}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.required}
                    onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                    disabled={saving}
                  />
                  <span>Documento obrigatório</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    disabled={saving}
                  />
                  <span>Ativo</span>
                </label>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.allow_multiple}
                  onChange={(e) => setFormData({ ...formData, allow_multiple: e.target.checked })}
                  disabled={saving}
                />
                <span style={{ fontWeight: 500 }}>Permitir adicionar + de um arquivo na mesma categoria</span>
              </label>
              {formData.allow_multiple && (
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '1.5rem' }}>
                  Útil para categorias como "Folha de Ponto" onde o usuário precisa fazer upload mensalmente
                </p>
              )}
            </div>

            {errors.submit && (
              <div style={{ padding: '0.75rem', background: '#fee2e2', color: '#ef4444', borderRadius: '6px', fontSize: '0.875rem' }}>
                {errors.submit}
              </div>
            )}
          </div>
        </form>
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setCategoryToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message={`Deseja realmente excluir a categoria "${categoryToDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
      />
    </div>
  )
}

export default DocumentCategoriesPage

